import { Card, Button, Image, Modal, Tag } from "antd";
import "react-phone-input-2/lib/style.css";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiPath from "../constants/apiPath";
import { ArrowLeftOutlined, ExclamationCircleOutlined, UserOutlined } from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { Severty, ShowToast } from "../helper/toast";
import moment from "moment";

let baseURL = apiPath.customerManager;

// Same status → { color, label } mapping as the Customer Management list
// (User.js) — kept in sync so a client's status reads identically whether
// you're looking at the table or this detail page.
const ACTIVITY_STATUS_CONFIG = {
  Paused: { color: "gold", label: "Paused" },
  Active: { color: "green", label: "Active" },
  Inactive: { color: "red", label: "Inactive" },
};
const ACCOUNT_STATUS_CONFIG = {
  payment_overdue: { color: "red", label: "Payment Overdue" },
  // Distinct from "Payment Overdue" — that means the contract's own term is
  // currently running but unpaid (urgent, client locked out today); this
  // means the term itself already ended with nothing further on file.
  contract_expired: { color: "default", label: "Contract Expired" },
  not_started: { color: "default", label: "Not Started" },
  upcoming: { color: "blue", label: "Upcoming Contract" },
  renewal_due_soon: { color: "orange", label: "Renewal Due Soon" },
  paid: { color: "green", label: "Paid" },
  no_contract: { color: "default", label: "No Contract" },
};

// One label/value pair in the details grid. Empty values render as a muted
// dash (same convention as the Customer Management table) instead of a
// bare "N/A" competing visually with real data.
const DetailField = ({ label, value }) => (
  <div className="cvp-field">
    <span className="cvp-field-label">{label}</span>
    <span className="cvp-field-value">{value || <span className="cvp-empty">—</span>}</span>
  </div>
);

function ViewUser() {
  const [record, setRecord] = useState({});
  const [loading, setLoading] = useState(false);
  const params = useParams();
  const { confirm } = Modal;
  const navigate = useNavigate();
  const { request } = useRequest();

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    setLoading(true);
    fetchData(params.id);
  }, [params.id]);


  const handleDocumentApproval = (id, isApproved) => {
    console.log("Document ID:", id);
    request({
      url: `/admin/auth/document-approval/${id}?isApproved=${isApproved}`,
      method: "PUT",
      data: { isApproved },
      onSuccess: () => {
        ShowToast("Document status updated successfully", Severty.SUCCESS);
        fetchData(id); // Refresh data
      },
      onError: (error) => {
        ShowToast(error, Severty.ERROR);
      },
    });
  };


  const showApprovalModal = (record) => {
    confirm({
      icon: <ExclamationCircleOutlined />,
      content: <p>Are you sure you want to approve or reject this document?</p>,
      okText: "Approve",
      cancelText: "Reject",
      onOk() {
        // Call API to approve
        handleDocumentApproval(record._id, true);
      },
      onCancel() {
        // Call API to reject
        handleDocumentApproval(record._id, false);
      },
    });
  };
  const fetchData = (id) => {
    request({
      url: `/admin/customer/user-detail/${id}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setRecord(data.data);
      },
      onError: (error) => {
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const activityStatusKey = record?.isPaused ? "Paused" : record?.activityActive ? "Active" : "Inactive";
  const activityStatus = ACTIVITY_STATUS_CONFIG[activityStatusKey];
  const accountStatus = ACCOUNT_STATUS_CONFIG[record?.accountStatus] || ACCOUNT_STATUS_CONFIG.no_contract;
  const accountStatusDate =
    record?.accountStatus === "not_started" && record?.accountStatusContractStartsOn
      ? `contract starts ${moment(record.accountStatusContractStartsOn).format("DD MMM YYYY")}`
      : record?.accountStatus === "upcoming" && record?.accountStatusPaidFrom
      ? `starts ${moment(record.accountStatusPaidFrom).format("DD MMM YYYY")}`
      : record?.accountStatusPaidUntil
      ? `paid until ${moment(record.accountStatusPaidUntil).format("DD MMM YYYY")}`
      : null;

  return (
    <div className="customer-view-page">
      <Button onClick={handleBack} className="back-button mb-2">
        <ArrowLeftOutlined /> Back
      </Button>

      <Card loading={loading} className="cvp-card">
        {/* Identity header — avatar, name, email, and both status pills up
            front, since "who is this and what state are they in" is what
            an admin actually opens this page to check first. */}
        <div className="cvp-header">
          <div className="cvp-avatar">
            {record?.image ? (
              <Image src={record.image} alt="Profile" preview={{ mask: "View" }} />
            ) : (
              <UserOutlined />
            )}
          </div>
          <div className="cvp-header-info">
            <h1 className="cvp-name">{record?.name || "—"}</h1>
            <div className="cvp-email">{record?.email || "—"}</div>
            <div className="cvp-status-row">
              <Tag color={activityStatus.color}>{activityStatus.label}</Tag>
              <Tag color={accountStatus.color}>
                {accountStatus.label}
                {accountStatusDate ? ` · ${accountStatusDate}` : ""}
              </Tag>
            </div>
          </div>
        </div>

        <div className="cvp-divider" />

        {/* Details grid — was a single stacked list of label/value rows
            (each its own full-width bordered strip); a 2-column grid reads
            faster and doesn't force scrolling for what's really just 4
            short facts. */}
        <div className="cvp-section-title">Account Details</div>
        <div className="cvp-grid">
          <DetailField label="Brand Manager" value={record?.brandManager?.map((d) => d.name).join(", ")} />
          <DetailField label="Brand Strategist" value={record?.brandStrategist?.map((d) => d.name).join(", ")} />
          <DetailField label="Posts" value={record?.postCount ?? "0"} />
          <DetailField
            label="Registration Date"
            value={record?.created_at ? moment(record.created_at).format("DD MMM YYYY") : null}
          />
        </div>
      </Card>
    </div>
  );
}

export default ViewUser;
